import { interviewCoachApi } from '@/services/interview-coach/api'
import type { LiveInterviewSessionSummary } from '@/services/interview-coach/types'
import { useEffect, useMemo, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { isLiveInterviewSignalActive } from './live-interview-signal.util'

const POLL_MS = 10000

export function useActiveLiveInterview() {
    const location = useLocation()
    const [sessions, setSessions] = useState<LiveInterviewSessionSummary[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let cancelled = false

        const refresh = async () => {
            try {
                // The global banner only needs currently active sessions. Loading the
                // full 100-session history on every route and every poll made unrelated
                // pages pay for a large Mongo projection.
                const res = await interviewCoachApi.listSessions({ limit: 25, status: 'active' })
                if (!cancelled) {
                    setSessions(res ?? [])
                }
            } catch {
                if (!cancelled) setSessions([])
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        void refresh()
        const timer = window.setInterval(refresh, POLL_MS)
        return () => {
            cancelled = true
            window.clearInterval(timer)
        }
    }, [])

    const activeSessions = useMemo(
        () =>
            sessions
                .filter(isLiveInterviewSignalActive)
                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()),
        [sessions]
    )

    const currentLiveMatch = matchPath('/dashboard/interviews/:sessionId/live', location.pathname)
    const currentLiveSessionId = currentLiveMatch?.params.sessionId
    const workbenchMatch =
        matchPath('/dashboard/interviews/:sessionId/:tab', location.pathname) ??
        matchPath('/dashboard/interviews/:sessionId', location.pathname)
    const currentWorkbenchSessionId = workbenchMatch?.params.sessionId

    const bannerSession = useMemo(() => {
        if (activeSessions.length === 0) return null
        // Already inside this session's workbench (HEFT, Review, Live, etc.) — no global nudge.
        const offWorkbench = activeSessions.filter(
            session => session.sessionId !== currentWorkbenchSessionId,
        )
        if (offWorkbench.length === 0) return null
        return offWorkbench[0] ?? null
    }, [activeSessions, currentWorkbenchSessionId])

    return {
        loading,
        activeSessions,
        bannerSession,
        currentLiveSessionId,
        currentWorkbenchSessionId,
    }
}
